import React, { useEffect, useState, useContext, useRef } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { Device } from '@twilio/voice-sdk';
import { getApp } from 'firebase/app';
import { getFirestore, collection, doc, setDoc, updateDoc } from 'firebase/firestore';
import Header from "components/Header/Header.js";
import HomeFooter from "components/Footer/HomeFooter.js";
import HeaderLinks from "components/Header/HeaderLinks.js";
import AmazonIcon from "../assets/img/amazon.png";
import SnoonuIcon from "../assets/img/snoonu.png";
import {
    Box, 
    Typography, 
    TextField, 
    Button, 
    Grid, 
    CircularProgress, 
    Fade,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions
} from '@mui/material';
import { ThemeContext } from "../components/Theme/ThemeContext";
import { useSelector, useDispatch } from "react-redux";
import { api } from 'common';
import { 
    Phone, 
    AlertTriangle, 
    Lightbulb, 
    Ban, 
    Truck, 
    ShieldAlert, 
    FileText, 
    PenTool, 
    PhoneCall,
    Car,
    AlertCircle,
    MessageSquare,
    Mic,
    MicOff,
    PhoneOff,
    Clock,
    CheckCircle2,
    ShieldCheck,
    Volume2,
    Maximize2,
    Disc,
    Octagon
} from 'lucide-react';

import { useTranslation } from "react-i18next";

const dashboardRoutes = [];

const contactReasons = [
    { id: 'lights_on', label: 'Vehicle lights are ON', icon: Lightbulb, color: '#f59e0b' },
    { id: 'no_parking', label: 'Vehicle is in a No Parking Zone', icon: Ban, color: '#ef4444' },
    { id: 'towed', label: 'Vehicle is being Towed', icon: Truck, color: '#dc2626' },
    { id: 'baby_pet', label: 'Baby or pet is inside the Vehicle', icon: ShieldAlert, color: '#b91c1c' },
    { id: 'windows_open', label: 'The Windows of the Vehicle are Open', icon: Maximize2, color: '#3b82f6' },
    { id: 'damage', label: 'Some Damage has occurred to the Vehicle', icon: AlertTriangle, color: '#d97706' },
    { id: 'flat_tyre', label: "Vehicle's tyres have gone flat", icon: Disc, color: '#ea580c' },
    { id: 'blocking', label: 'Vehicle is blocking driveway/entrance', icon: Octagon, color: '#d97706' }
];

const mockVehicle = {
    name: "Vehicle Owner (Preview Mode)",
    phone: "+97400000000",
    whatsapp: "+97400000000",
    vehicleNumber: "RAHL-777",
    vehicleType: "sedan",
    brandModel: "Preview Vehicle",
    activated: true,
    showNumber: false
};

export default function LandingPage(props) {
    const { t, i18n } = useTranslation();
    const isRTL = i18n?.language === 'ar' || i18n?.language === 'ara' || (typeof i18n?.language === 'string' && i18n.language.startsWith('ar'));
    const { isDark, currentTheme } = useContext(ThemeContext);
    const dispatch = useDispatch();
    const { verifyQRCode, loginWithAdminSerial } = api;
    const { ...rest } = props;
    const { adminSerial } = useParams();
    const navigate = useNavigate();

    const auth = useSelector(state => state.auth);
    const [role, setRole] = useState();

    const [serial, setSerial] = useState('');
    const [loading, setLoading] = useState(false);
    const [vehicle, setVehicle] = useState(null);
    const [error, setError] = useState(null);

    const [selectedReason, setSelectedReason] = useState('');
    const [customMessage, setCustomMessage] = useState('');
    const [showEmergencyMenu, setShowEmergencyMenu] = useState(false);
    const [showCallerPrompt, setShowCallerPrompt] = useState(false);
    const [callerPhone, setCallerPhone] = useState('');
    const [connectingCall, setConnectingCall] = useState(false);
    const [promptAction, setPromptAction] = useState('call');

    const [activeCall, setActiveCall] = useState(null);
    const [webrtcStatus, setWebrtcStatus] = useState('idle');
    const [twilioDevice, setTwilioDevice] = useState(null);

    // New UX & Safety Features
    const [showContactSelector, setShowContactSelector] = useState(false);
    const [rateLimitModal, setRateLimitModal] = useState({ open: false, type: '', remainingMins: 0, limit: 0 });
    const [messageSentPopup, setMessageSentPopup] = useState(false);
    const [smsRemainingCount, setSmsRemainingCount] = useState(null);
    const [isMuted, setIsMuted] = useState(false);
    const [callTimer, setCallTimer] = useState(0);

    const [selectedTargetChoice, setSelectedTargetChoice] = useState('primary');
    const callLogKeyRef = useRef(null);
    const callTimerRef = useRef(0);

    // Rate Limiting Rules (2 Calls / 15 mins, 3 SMS / 15 mins)
    const CALL_LIMIT = 2;
    const CALL_WINDOW_MS = 15 * 60 * 1000;
    const SMS_LIMIT = 3;
    const SMS_WINDOW_MS = 15 * 60 * 1000;

    const checkRateLimit = (type, vehicleSerial) => {
        const key = `rahl_rate_${type}_${vehicleSerial || 'default'}`;
        const limit = type === 'call' ? CALL_LIMIT : SMS_LIMIT;
        const windowMs = type === 'call' ? CALL_WINDOW_MS : SMS_WINDOW_MS;
        try {
            const stored = JSON.parse(localStorage.getItem(key) || '[]');
            const now = Date.now();
            const valid = stored.filter(ts => now - ts < windowMs);
            if (valid.length >= limit) {
                const oldest = Math.min(...valid);
                const remMs = windowMs - (now - oldest);
                const remMins = Math.ceil(remMs / 60000);
                return { allowed: false, remainingMins: remMins || 1, limit };
            }
            return { allowed: true, valid, key };
        } catch (e) {
            return { allowed: true, valid: [], key };
        }
    };

    const recordRateLimitUsage = (type, vehicleSerial) => {
        const key = `rahl_rate_${type}_${vehicleSerial || 'default'}`;
        const windowMs = type === 'call' ? CALL_WINDOW_MS : SMS_WINDOW_MS;
        try {
            const stored = JSON.parse(localStorage.getItem(key) || '[]');
            const now = Date.now();
            const valid = stored.filter(ts => now - ts < windowMs);
            valid.push(now);
            localStorage.setItem(key, JSON.stringify(valid));
        } catch (e) {
            console.error(e);
        }
    };

    const MAX_CALL_DURATION_SECS = 60; // 1-minute maximum call limit

    useEffect(() => {
        let interval = null;
        if (webrtcStatus === 'connected') {
            interval = setInterval(() => {
                setCallTimer((prev) => {
                    const nextVal = prev + 1;
                    callTimerRef.current = nextVal;
                    if (nextVal >= MAX_CALL_DURATION_SECS) {
                        handleWebRTCHangup();
                        return MAX_CALL_DURATION_SECS;
                    }
                    return nextVal;
                });
            }, 1000);
        } else {
            setCallTimer(0);
            callTimerRef.current = 0;
            setIsMuted(false);
        }
        return () => {
            if (interval) clearInterval(interval);
        };
    }, [webrtcStatus]);

    const toggleMute = () => {
        if (activeCall) {
            const newMute = !isMuted;
            activeCall.mute(newMute);
            setIsMuted(newMute);
        }
    };

    const formatCallTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins < 10 ? '0' : ''}${mins}:${secs < 10 ? '0' : ''}${secs}`;
    };

    useEffect(() => {
        return () => {
            if (twilioDevice) {
                twilioDevice.destroy();
            }
        };
    }, [twilioDevice]);

    useEffect(() => {
        if (auth && auth.profile && auth.profile.uid) {
            setRole(auth.profile.usertype);
        }
    }, [auth]);

    const getSecondaryContactTitle = () => {
        if (!vehicle?.secondaryName) {
            return isRTL ? 'جهة الاتصال الثانوية / الطوارئ' : 'Emergency / Secondary Contact';
        }
        const sName = vehicle.secondaryName.trim();
        const defaultNames = [
            'Secondary / Emergency Contact',
            'Emergency Contact',
            'Secondary Contact',
            'Secondary Driver / Emergency',
            'Secondary Owner',
            'Secondary Driver',
            'Secondary / Emergency Contact'
        ];
        if (defaultNames.includes(sName)) {
            return isRTL ? 'جهة الاتصال الثانوية / الطوارئ' : 'Emergency / Secondary Contact';
        }
        // Translate common English labels to Arabic
        if (isRTL) {
            const labelTranslations = {
                'home': 'المنزل',
                'work': 'العمل',
                'office': 'المكتب',
                'family': 'العائلة',
                'father': 'الأب',
                'mother': 'الأم',
                'brother': 'الأخ',
                'sister': 'الأخت',
                'spouse': 'الزوج/الزوجة',
                'wife': 'الزوجة',
                'husband': 'الزوج',
                'friend': 'صديق',
                'driver': 'السائق',
                'manager': 'المدير',
                'emergency': 'الطوارئ',
                'other': 'أخرى',
                'personal': 'شخصي',
                'main': 'الرئيسي',
                'mobile': 'الجوال',
                'landline': 'الهاتف الأرضي'
            };
            const lowerName = sName.toLowerCase();
            if (labelTranslations[lowerName]) {
                return labelTranslations[lowerName];
            }
        }
        return sName;
    };

    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        let serialParam = urlParams.get('serial') || urlParams.get('qr') || adminSerial;
        if (serialParam) {
            if (typeof serialParam === 'string') {
                serialParam = serialParam.trim();
                if (!serialParam.startsWith('RAHL')) {
                    try {
                        const decoded = window.atob(serialParam);
                        if (decoded && /^[A-Z0-9_-]+$/i.test(decoded)) {
                            serialParam = decoded;
                        }
                    } catch (e) {
                        // Keep original
                    }
                }
            }
            if (serialParam === 'RAHL-ADM99-LGN') {
                navigate('/login?admin=true');
                return;
            }
            setSerial(serialParam);
            fetchVehicleDetails(serialParam);
        }
    }, [adminSerial]);

    const fetchVehicleDetails = (serialCode) => {
        setLoading(true);
        setError(null);
        setVehicle(null);
        if (serialCode === 'MON-DEMO001-PREVIEW-MOCK') {
            setLoading(false);
            setVehicle({
                name: "Car Owner",
                phone: "+97455551234",
                secondaryPhone: "+97455555678",
                whatsapp: "+97455551234",
                vehicleNumber: "GJ06JN7413",
                vehicleType: "sedan",
                brandModel: "Toyota Land Cruiser",
                activated: true,
                showNumber: false
            });
            return;
        }
        dispatch(verifyQRCode(serialCode))
            .then((res) => {
                setLoading(false);
                if (res && res.valid) {
                    setVehicle(res.owner);
                } else {
                    setError(res?.error || 'Invalid or unregistered QR code/serial number.');
                }
            })
            .catch((err) => {
                setLoading(false);
                setError(err.message || 'An error occurred during verification.');
            });
    };

    const handleReset = () => {
        setSerial('');
        setVehicle(null);
        setError(null);
        setSelectedReason('');
        setCustomMessage('');
        setShowEmergencyMenu(false);
        setShowCallerPrompt(false);
        setCallerPhone('');
        setConnectingCall(false);
        setPromptAction('call');
        const newUrl = `${window.location.protocol}//${window.location.host}${window.location.pathname}`;
        window.history.pushState({ path: newUrl }, '', newUrl);
    };

    const handleCall = () => {
        if (vehicle && vehicle.phone) {
            window.location.href = `tel:${vehicle.phone}`;
        }
    };

    const handleEmergencyCall = (number) => {
        window.location.href = `tel:${number}`;
    };

    const handleSMS = () => {
        if (vehicle && vehicle.phone) {
            const plate = vehicle.vehicleNumber || '';
            const brand = vehicle.brandModel ? ` (${vehicle.brandModel})` : '';
            const vehicleStr = `${plate}${brand}`;
            let bodyText = '';

            switch (selectedReason) {
                case 'lights_on':
                    bodyText = `Hello, I noticed the lights are ON on your vehicle ${vehicleStr}. Please check.`;
                    break;
                case 'no_parking':
                    bodyText = `Hello, your vehicle ${vehicleStr} is parked in a no-parking zone. Please move it.`;
                    break;
                case 'towed':
                    bodyText = `Hello, I noticed your vehicle ${vehicleStr} is being towed.`;
                    break;
                case 'baby_pet':
                    bodyText = `URGENT: There is a baby or pet left inside your vehicle ${vehicleStr}. Please check immediately!`;
                    break;
                case 'windows_open':
                    bodyText = `Hello, the windows of your vehicle ${vehicleStr} are open.`;
                    break;
                case 'damage':
                    bodyText = `Hello, I noticed some damage has occurred to your vehicle ${vehicleStr}.`;
                    break;
                case 'flat_tyre':
                    bodyText = `Hello, your vehicle ${vehicleStr} has a flat tire.`;
                    break;
                case 'blocking':
                    bodyText = `Hello, your vehicle ${vehicleStr} is blocking a driveway or entrance. Please move it.`;
                    break;
                case 'custom':
                    bodyText = customMessage || `Hello, regarding your vehicle ${vehicleStr}.`;
                    break;
                default:
                    bodyText = `Hello, regarding your vehicle ${vehicleStr}.`;
            }

            const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
            const separator = isIOS ? '&' : '?';
            window.location.href = `sms:${vehicle.phone}${separator}body=${encodeURIComponent(bodyText)}`;
        }
    };

    const handleMaskedCall = () => {
        if (!callerPhone || callerPhone.trim() === '') {
            alert(isRTL ? 'يرجى إدخال رقم هاتفك للمتابعة.' : 'Please enter your phone number to connect.');
            return;
        }

        if (promptAction === 'webrtc') {
            setShowCallerPrompt(false);
            handleWebRTCCall(selectedTargetChoice, callerPhone);
            return;
        }

        if (promptAction !== 'sms') {
            const check = checkRateLimit('call', serial);
            if (!check.allowed) {
                setRateLimitModal({
                    open: true,
                    type: 'call',
                    remainingMins: check.remainingMins,
                    limit: check.limit
                });
                return;
            }
            recordRateLimitUsage('call', serial);
        }

        setConnectingCall(true);

        const apiPrefix = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
            ? 'http://localhost:5001/move-773be/us-central1'
            : 'https://us-central1-move-773be.cloudfunctions.net';

        axios.post(`${apiPrefix}/initiate_masked_call`, {
            caller_phone: callerPhone,
            serial: serial
        })
        .then(res => {
            setConnectingCall(false);
            setShowCallerPrompt(false);
            if (res.data && res.data.virtual_number) {
                if (promptAction === 'sms') {
                    const plate = vehicle.vehicleNumber || '';
                    const brand = vehicle.brandModel ? ` (${vehicle.brandModel})` : '';
                    const vehicleStr = `${plate}${brand}`;
                    let bodyText = `Hello, regarding your vehicle ${vehicleStr}.`;

                    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
                    const separator = isIOS ? '&' : '?';
                    window.location.href = `sms:${res.data.virtual_number}${separator}body=${encodeURIComponent(bodyText)}`;
                } else {
                    window.location.href = `tel:${res.data.virtual_number}`;
                }
            } else {
                alert('Could not establish secure connection. Please try again.');
            }
        })
        .catch(err => {
            setConnectingCall(false);
            alert(err.response?.data?.error || err.message || 'Failed to initiate secure call.');
        });
    };

    const handleCallOwnerClick = () => {
        if (vehicle && vehicle.secondaryPhone && vehicle.secondaryPhone.trim().length >= 8) {
            setShowContactSelector(true);
        } else {
            initiateCallFlow('primary');
        }
    };

    const initiateCallFlow = (targetChoice = 'primary') => {
        setSelectedTargetChoice(targetChoice);
        setPromptAction('call');
        if (!callerPhone || callerPhone.trim().length === 0) {
            setShowCallerPrompt(true);
        } else {
            handleMaskedCall();
        }
    };

    const handleWebRTCCall = (targetChoice = 'primary', overridePhone = null) => {
        const activePhone = overridePhone || callerPhone;
        if (!activePhone || activePhone.trim().length === 0) {
            setSelectedTargetChoice(targetChoice);
            setPromptAction('webrtc');
            setShowCallerPrompt(true);
            return;
        }

        const check = checkRateLimit('call', serial);
        if (!check.allowed) {
            setRateLimitModal({
                open: true,
                type: 'call',
                remainingMins: check.remainingMins,
                limit: check.limit
            });
            return;
        }

        recordRateLimitUsage('call', serial);
        setWebrtcStatus('connecting');

        const logCallStart = () => {
            try {
                const firestoreDb = getFirestore(getApp(), 'scanme');
                const newLogRef = doc(collection(firestoreDb, 'callLogs'));
                callLogKeyRef.current = newLogRef.id;
                setDoc(newLogRef, {
                    id: newLogRef.id,
                    callerPhone: activePhone || 'Anonymous Scanner',
                    serial: serial || 'DEMORAHL001',
                    vehicleNumber: vehicle?.vehicleNumber || 'RAHL-777',
                    target: targetChoice || 'primary',
                    timestamp: Date.now(),
                    startTime: new Date().toISOString(),
                    callDurationSecs: 0,
                    callDurationText: '00:00',
                    status: 'Connected'
                });
            } catch (e) {
                console.error('Error logging call start:', e);
            }
        };

        const apiPrefix = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
            ? 'http://localhost:5001/move-773be/us-central1'
            : 'https://us-central1-move-773be.cloudfunctions.net';

        axios.get(`${apiPrefix}/twilio_webrtc_token`)
        .then(async (res) => {
            if (res.data && res.data.token) {
                const token = res.data.token;
                
                const device = new Device(token, {
                    codecPreferences: ['opus', 'pcmu'],
                    fakeLocalDTMF: true,
                    enableIceRestart: true
                });

                setTwilioDevice(device);

                device.on('error', (error) => {
                    console.error('Twilio WebRTC Error:', error);
                    setWebrtcStatus('idle');
                    alert(`Calling Error: ${error.message}`);
                });

                const call = await device.connect({ params: { serial: serial, target: targetChoice } });
                setActiveCall(call);

                let callStartTime = null;

                call.on('accept', () => {
                    callStartTime = Date.now();
                    setWebrtcStatus('connected');
                    logCallStart();
                });

                call.on('disconnect', () => {
                    handleWebRTCHangup();
                    if (callStartTime && (Date.now() - callStartTime < 3500)) {
                        alert('Call disconnected by Twilio.\n\nOn Twilio Trial Accounts:\n1. Ensure the owner phone number includes country code (e.g. +91... or +974...).\n2. On trial accounts, target numbers must be added under Twilio Console > Verified Caller IDs.');
                    }
                });

                call.on('reject', () => {
                    handleWebRTCHangup();
                    alert('Call rejected by Twilio. Please verify destination phone number in Twilio Console.');
                });
            } else {
                setWebrtcStatus('idle');
                alert('Could not fetch call token.');
            }
        })
        .catch(err => {
            console.warn('WebRTC token fetch failed. Falling back to call simulator for demo.', err);
            setWebrtcStatus('connecting');
            setTimeout(() => {
                setWebrtcStatus('connected');
                logCallStart();
            }, 2500);
        });
    };

    const handleWebRTCHangup = () => {
        if (callLogKeyRef.current) {
            try {
                const firestoreDb = getFirestore(getApp(), 'scanme');
                const durationSecs = callTimerRef.current || 0;
                const mins = Math.floor(durationSecs / 60);
                const secs = durationSecs % 60;
                const durationText = `${mins < 10 ? '0' : ''}${mins}:${secs < 10 ? '0' : ''}${secs}`;
                updateDoc(doc(firestoreDb, 'callLogs', callLogKeyRef.current), {
                    callDurationSecs: durationSecs,
                    callDurationText: durationText,
                    status: 'Completed',
                    endTime: new Date().toISOString()
                });
            } catch (e) {
                console.error('Error updating call log on hangup:', e);
            }
            callLogKeyRef.current = null;
        }

        try {
            if (activeCall) {
                activeCall.disconnect();
            }
        } catch (e) {
            console.warn("Error disconnecting active call:", e);
        }
        try {
            if (twilioDevice) {
                twilioDevice.destroy();
            }
        } catch (e) {
            console.warn("Error destroying twilio device:", e);
        }
        setTwilioDevice(null);
        setActiveCall(null);
        setWebrtcStatus('idle');
        setCallTimer(0);
        callTimerRef.current = 0;
        setIsMuted(false);
    };

    const [sendingAlert, setSendingAlert] = useState(false);
    const [alertSuccess, setAlertSuccess] = useState(false);

    const sendAlert = (reason, customTxt = '') => {
        const check = checkRateLimit('sms', serial);
        if (!check.allowed) {
            setRateLimitModal({
                open: true,
                type: 'sms',
                remainingMins: check.remainingMins,
                limit: check.limit
            });
            return;
        }

        setSendingAlert(true);
        setAlertSuccess(false);

        const apiPrefix = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
            ? 'http://localhost:5001/move-773be/us-central1'
            : 'https://us-central1-move-773be.cloudfunctions.net';

        axios.post(`${apiPrefix}/send_alert_sms`, {
            serial: serial,
            reason: reason,
            customMessage: customTxt
        })
        .then(res => {
            recordRateLimitUsage('sms', serial);
            setSendingAlert(false);
            setAlertSuccess(true);
            setSmsRemainingCount(Math.max(SMS_LIMIT - (check.valid.length + 1), 0));
            setMessageSentPopup(true);
            setCustomMessage('');
            setTimeout(() => {
                setAlertSuccess(false);
            }, 5000);
        })
        .catch(err => {
            setSendingAlert(false);
            alert(err.response?.data?.error || err.message || 'Failed to notify vehicle owner.');
        });
    };

    const handleReasonSelect = (reason) => {
        if (sendingAlert) return;
        setSelectedReason(reason);
        if (reason !== 'custom') {
            sendAlert(reason);
        }
    };

    const renderReasons = () => {
        return (
            <Box>
                {/* Alert Status Banner */}
                {(sendingAlert || alertSuccess) && (
                    <Box sx={{
                        mb: 3,
                        p: 1.5,
                        borderRadius: '16px',
                        backgroundColor: alertSuccess ? 'rgba(34, 197, 94, 0.15)' : 'rgba(233, 174, 49, 0.15)',
                        border: `1px solid ${alertSuccess ? '#22c55e' : '#E9AE31'}`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 1.5
                    }}>
                        {sendingAlert ? (
                            <>
                                <CircularProgress size={16} sx={{ color: '#E9AE31' }} />
                                <Typography variant="body2" sx={{ color: '#E9AE31', fontWeight: 'bold', fontSize: '13px' }}>
                                    Notifying vehicle owner...
                                </Typography>
                            </>
                        ) : (
                            <>
                                <Box sx={{
                                    width: 18,
                                    height: 18,
                                    borderRadius: '50%',
                                    backgroundColor: '#22c55e',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: '#ffffff',
                                    fontWeight: 'bold',
                                    fontSize: '11px'
                                }}>✓</Box>
                                <Typography variant="body2" sx={{ color: '#22c55e', fontWeight: 'bold', fontSize: '13px' }}>
                                    Owner notified successfully!
                                </Typography>
                            </>
                        )}
                    </Box>
                )}

                <Typography variant="body1" sx={{ mb: 3, fontWeight: '700', color: currentTheme.textMain, opacity: 0.9 }}>
                    {t('select_reason_subtitle', 'Please select the reason below for contacting the owner:')}
                </Typography>
                
                <Grid container spacing={2}>
                    {contactReasons.map((reason) => {
                        const IconComponent = reason.icon;
                        const isSelected = selectedReason === reason.id;
                        return (
                            <Grid item xs={12} sm={6} key={reason.id}>
                                <Box 
                                    onClick={() => handleReasonSelect(reason.id)}
                                    sx={{
                                        cursor: 'pointer',
                                        backgroundColor: isSelected ? 'rgba(233, 174, 49, 0.08)' : isDark ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.01)',
                                        border: isSelected ? '2px solid #E9AE31' : '1px solid ' + (isDark ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.06)'),
                                        borderRadius: '16px',
                                        p: 2,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 2,
                                        transition: 'all 0.2s ease-in-out',
                                        '&:hover': {
                                            transform: 'translateY(-1px)',
                                            backgroundColor: isSelected ? 'rgba(233, 174, 49, 0.1)' : 'rgba(100, 100, 100, 0.03)',
                                            borderColor: isSelected ? '#E9AE31' : 'rgba(233, 174, 49, 0.4)'
                                        }
                                    }}
                                >
                                    <Box 
                                        sx={{
                                            backgroundColor: isSelected ? '#E9AE31' : 'rgba(100, 100, 100, 0.08)',
                                            borderRadius: '10px',
                                            p: 1.2,
                                            flexShrink: 0,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            color: isSelected ? '#FFFFFF' : reason.color,
                                            transition: 'all 0.2s'
                                        }}
                                    >
                                        <IconComponent size={20} />
                                    </Box>
                                    <Typography sx={{ fontWeight: '600', color: currentTheme.textMain, fontSize: '14px' }}>
                                        {t('reason_' + reason.id, reason.label)}
                                    </Typography>
                                </Box>
                            </Grid>
                        );
                    })}
                </Grid>

                {selectedReason === 'custom' && (
                    <Fade in={selectedReason === 'custom'}>
                        <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                            <TextField
                                fullWidth
                                placeholder="Type your custom message here..."
                                variant="outlined"
                                multiline
                                rows={3}
                                value={customMessage}
                                onChange={(e) => setCustomMessage(e.target.value)}
                                sx={{
                                    '& label.Mui-focused': { color: '#E9AE31' },
                                    '& .MuiOutlinedInput-root': {
                                        '&.Mui-focused fieldset': { borderColor: '#E9AE31' },
                                        borderRadius: '16px',
                                        color: currentTheme.textMain,
                                        backgroundColor: isDark ? 'rgba(255, 255, 255, 0.01)' : 'rgba(0, 0, 0, 0.01)'
                                    }
                                }}
                            />
                            <Button
                                fullWidth
                                variant="contained"
                                onClick={() => sendAlert('custom', customMessage)}
                                disabled={!customMessage.trim() || sendingAlert}
                                sx={{
                                    py: 1.5,
                                    borderRadius: '16px',
                                    backgroundColor: '#E9AE31',
                                    color: '#000000',
                                    fontWeight: 'bold',
                                    textTransform: 'none',
                                    boxShadow: '0 4px 12px rgba(233, 174, 49, 0.2)',
                                    '&:hover': {
                                        backgroundColor: '#d89e22',
                                        boxShadow: '0 6px 16px rgba(233, 174, 49, 0.3)'
                                    }
                                }}
                            >
                                {sendingAlert ? 'Sending...' : 'Send Message'}
                            </Button>
                        </Box>
                    </Fade>
                )}
            </Box>
        );
    };

    // 1. Direct Visit: Show a self-contained, non-scrolling Coming Soon page
    if (!serial) {
        return (
            <Box sx={{
                height: '100vh',
                overflow: 'hidden',
                backgroundColor: isDark ? '#08080a' : currentTheme.background,
                display: 'flex',
                flexDirection: 'column'
            }}>
                {/* Top Navigation Bar - Always visible and clickable */}
                <Header
                    color="transparent"
                    routes={dashboardRoutes}
                    rightLinks={<HeaderLinks />}
                    {...rest}
                />

                {/* Content area below header - fills remaining viewport, never scrolls */}
                <Box sx={{
                    position: 'relative',
                    flex: 1,
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    p: 2.5,
                    textAlign: 'center'
                }}>
                    {/* Decorative ambient glow background */}
                    <Box sx={{
                        position: 'absolute',
                        inset: 0,
                        pointerEvents: 'none',
                        background: isDark
                            ? 'radial-gradient(circle at 20% 20%, rgba(233, 174, 49, 0.10) 0%, transparent 45%), radial-gradient(circle at 80% 75%, rgba(233, 174, 49, 0.07) 0%, transparent 45%)'
                            : 'radial-gradient(circle at 20% 20%, rgba(233, 174, 49, 0.10) 0%, transparent 45%), radial-gradient(circle at 80% 75%, rgba(233, 174, 49, 0.06) 0%, transparent 45%)'
                    }} />

                    <Box sx={{
                        position: 'relative',
                        width: '100%',
                        maxWidth: '480px',
                        backgroundColor: isDark ? 'rgba(18, 18, 24, 0.92)' : '#ffffff',
                        border: '1.5px solid rgba(233, 174, 49, 0.4)',
                        borderRadius: '28px',
                        p: { xs: 4, sm: 5 },
                        boxShadow: isDark
                            ? '0 24px 50px rgba(0, 0, 0, 0.6), 0 0 25px rgba(233, 174, 49, 0.15)'
                            : '0 20px 45px rgba(233, 174, 49, 0.18), 0 8px 25px rgba(0, 0, 0, 0.08)',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center'
                    }}>
                        {/* Icon */}
                        <Box sx={{
                          width: '72px',
                          height: '72px',
                          borderRadius: '36px',
                          backgroundColor: 'rgba(233, 174, 49, 0.12)',
                          border: '1px solid rgba(233, 174, 49, 0.25)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#E9AE31',
                          mb: 2.5
                        }}>
                            <Car size={36} />
                        </Box>

                        <Typography variant="h3" sx={{ fontWeight: '950', fontSize: { xs: '30px', sm: '36px' }, color: '#E9AE31', mb: 1, letterSpacing: '-0.5px' }}>
                            {t('coming_soon', 'Coming Soon')}
                        </Typography>

                        {/* Gold Divider */}
                        <Box sx={{
                            width: 48,
                            height: 4,
                            backgroundColor: '#E9AE31',
                            borderRadius: '2px',
                            my: 1.5,
                            boxShadow: '0 0 10px rgba(233, 174, 49, 0.5)'
                        }} />

                        <Typography sx={{ color: isDark ? 'rgba(255, 255, 255, 0.85)' : '#475569', fontSize: '14px', lineHeight: 1.6, opacity: 0.95 }}>
                            {t('coming_soon_subtitle', 'Our smart vehicle contact portal is launching soon. Scan a RAHL QR code on a vehicle to get in touch with the owner securely and immediately.')}
                        </Typography>
                    </Box>
                </Box>
            </Box>
        );
    }

    // 2. Active QR Scan View: Full viewport split layout
    return (
        <Box sx={{
            backgroundColor: currentTheme.background,
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            color: currentTheme.textMain,
            transition: 'background-color 0.3s ease, color 0.3s ease',
            overflow: 'hidden',
            position: 'relative'
        }}>
            {/* Brand mark, pinned to the page's top-left corner */}
            <Box
                component="img"
                src={require("../assets/img/Icon.png").default}
                alt="RAHL"
                sx={{
                    position: 'absolute',
                    top: { xs: 12, md: 16 },
                    left: { xs: 12, md: 16 },
                    width: { xs: '36px', md: '44px' },
                    height: { xs: '36px', md: '44px' },
                    borderRadius: { xs: '10px', md: '12px' },
                    zIndex: 20
                }}
            />

            {/* Main Responsive Grid */}
            <Grid container sx={{
                flex: 1,
                height: { md: '100vh' },
                minHeight: { xs: '100vh', md: 'auto' }
            }}>
                
                {/* Left Side: Vehicle Info */}
                <Grid item xs={12} md={4.5} sx={{
                    background: isDark 
                        ? 'linear-gradient(135deg, #090909 0%, #151108 100%)' 
                        : 'linear-gradient(135deg, #f9f9f9 0%, #f3ede0 100%)',
                    borderRight: { md: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)'}` },
                    borderBottom: { xs: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)'}`, md: 'none' },
                    px: { xs: 2, md: 5 },
                    pb: { xs: 2, md: 5 },
                    pt: { xs: 7.5, md: 5 },
                    display: 'flex',
                    flexDirection: { xs: 'row', md: 'column' },
                    justifyContent: { xs: 'space-around', md: 'center' },
                    alignItems: 'center',
                    flexWrap: { xs: 'wrap', md: 'nowrap' },
                    gap: { xs: 2, md: 0 },
                    textAlign: { xs: 'left', md: 'left' }
                }}>

                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        width: { xs: '100%', sm: 'auto' },
                        justifyContent: { xs: 'center', sm: 'flex-start' }
                    }}>
                        <Box>
                            <Typography variant="h5" sx={{ fontWeight: '900', color: currentTheme.textMain, mb: 0.25, letterSpacing: '-0.5px', fontSize: { xs: '18px', md: '24px' } }}>
                                {t('rahl_vehicle_portal', 'RAHL Vehicle Portal')}
                            </Typography>

                            <Typography variant="caption" sx={{ color: currentTheme.textMuted, maxWidth: '340px', lineHeight: 1.5, display: { xs: 'none', sm: 'block' } }}>
                                {t('secure_contact_desc', 'Securely contact the vehicle owner regarding parking blockages.')}
                            </Typography>
                        </Box>
                    </Box>

                    {/* License Plate */}
                    {!loading && vehicle && (
                        <Box sx={{ 
                            width: { xs: '100%', sm: 'auto' }, 
                            maxWidth: '300px',
                            display: 'flex',
                            justifyContent: 'center'
                        }}>
                            <Box sx={{
                                border: '2px solid #E9AE31',
                                borderRadius: '14px',
                                px: { xs: 3, md: 2 },
                                py: { xs: 1, md: 1.8 },
                                backgroundColor: '#FFFFFF',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                boxShadow: '0 8px 24px rgba(233, 174, 49, 0.12)',
                                width: { xs: '100%', sm: '220px', md: '100%' }
                            }}>
                                <Typography sx={{ fontSize: '9px', fontWeight: '800', letterSpacing: '2px', color: '#777 !important', mb: 0.1 }}>
                                    {t('license_plate', 'LICENSE PLATE')}
                                </Typography>
                                <Typography variant="h4" sx={{ fontWeight: '900', letterSpacing: '1px', m: 0, color: '#000000 !important', fontSize: { xs: '20px', md: '28px' } }}>
                                    {vehicle.vehicleNumber === 'UNREGISTERED' ? (isRTL ? 'غير مسجّل' : 'UNREGISTERED') : vehicle.vehicleNumber}
                                </Typography>
                                <Typography sx={{ fontSize: '10px', fontWeight: '800', color: '#444 !important', mt: 0.1, textTransform: 'uppercase' }}>
                                    {vehicle.vehicleType} {vehicle.brandModel ? ' - ' + vehicle.brandModel : ''}
                                </Typography>
                            </Box>
                        </Box>
                    )}
                </Grid>

                {/* Right Side: Reasons */}
                <Grid item xs={12} md={7.5} sx={{ 
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    height: { md: '100%' },
                    backgroundColor: currentTheme.background,
                    position: 'relative'
                }}>
                    
                    {/* Reason Grid container */}
                    <Box sx={{
                        flex: 1,
                        overflowY: 'auto',
                        p: { xs: 2.5, sm: 3, md: 4 },
                        pb: { xs: '120px', md: 4 }
                    }}>
                        
                        {/* Loading State */}
                        {loading && (
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8 }}>
                                <CircularProgress size={36} sx={{ color: '#E9AE31', mb: 2 }} />
                                <Typography sx={{ color: currentTheme.textMuted, fontSize: '13px' }}>
                                    {isRTL ? 'جاري التحقق من رمز الاستجابة السريعة...' : 'Verifying QR code...'}
                                </Typography>
                            </Box>
                        )}

                        {/* Error State */}
                        {!loading && error && (
                            <Box sx={{ py: 4, textAlign: 'center', maxWidth: '440px', mx: 'auto' }}>
                                <AlertCircle size={44} color="#ef4444" style={{ marginBottom: '12px' }} />
                                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 0.5, color: '#ef4444' }}>
                                    {isRTL ? 'فشل التحقق' : 'Verification Failed'}
                                </Typography>
                                <Typography sx={{ mb: 3, fontSize: '13px', color: currentTheme.textMuted }}>
                                    {error}
                                </Typography>
                                <Button
                                    variant="outlined"
                                    onClick={handleReset}
                                    sx={{
                                        borderRadius: '20px',
                                        px: 3.5,
                                        py: 1,
                                        borderColor: '#E9AE31',
                                        color: '#E9AE31',
                                        textTransform: 'none',
                                        fontWeight: 'bold',
                                        fontSize: '13px',
                                        '&:hover': {
                                            borderColor: '#D19825',
                                            backgroundColor: 'rgba(233, 174, 49, 0.05)'
                                        }
                                    }}
                                >
                                    {isRTL ? 'إعادة المحاولة' : 'Clear & Reset'}
                                </Button>
                            </Box>
                        )}

                        {/* Verified Vehicle Contact Flow */}
                        {!loading && vehicle && (
                            <Box>
                                {vehicle.activated === false ? (
                                    <Box sx={{ textAlign: 'center', py: 4, maxWidth: '440px', mx: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                        <AlertTriangle size={48} color="#ef4444" style={{ marginBottom: '12px' }} />
                                        <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 1, color: '#ef4444' }}>
                                            {isRTL ? 'الحساب غير مفعّل' : 'Account Not Activated'}
                                        </Typography>
                                        <Typography sx={{ color: currentTheme.textMuted, fontSize: '13px', lineHeight: 1.5, mb: 3 }}>
                                            {isRTL 
                                                ? 'لم يتم تسجيل أو تفعيل رمز الاستجابة السريعة (QR) من قبل مالك المركبة بعد.' 
                                                : 'This QR code has not been registered or activated by the vehicle owner yet.'}
                                        </Typography>
                                        <Button
                                            variant="contained"
                                            onClick={() => window.open('https://rahl.inspire.qa/download', '_blank')}
                                            sx={{
                                                borderRadius: '24px',
                                                py: 1.5,
                                                px: 4,
                                                backgroundColor: '#E9AE31',
                                                color: '#FFFFFF',
                                                fontWeight: 'bold',
                                                fontSize: '14px',
                                                textTransform: 'none',
                                                boxShadow: 'none',
                                                '&:hover': {
                                                    backgroundColor: '#D19825',
                                                    boxShadow: 'none'
                                                }
                                            }}
                                        >
                                            {isRTL ? 'تحميل التطبيق والتسجيل' : 'Download App & Register'}
                                        </Button>
                                    </Box>
                                ) : (
                                    <Box>
                                        <Typography variant="body2" sx={{ mb: 1, fontWeight: '700', color: currentTheme.textMain, opacity: 0.85, fontSize: { xs: '12px', md: '13px' } }}>
                                            {t('select_reason_subtitle', 'Please select the reason below for contacting the owner:')}
                                        </Typography>

                                        <Grid container spacing={1.5} sx={{
                                            opacity: sendingAlert ? 0.5 : 1,
                                            pointerEvents: sendingAlert ? 'none' : 'auto',
                                            transition: 'opacity 0.15s ease-in-out'
                                        }}>
                                            {contactReasons.map((reason) => {
                                                const IconComponent = reason.icon;
                                                const isSelected = selectedReason === reason.id;
                                                return (
                                                    <Grid item xs={12} key={reason.id} onClick={() => {
                                                                handleReasonSelect(reason.id);
                                                                setShowEmergencyMenu(false);
                                                            }}>
                                                        <Box
                                                            sx={{
                                                                cursor: sendingAlert ? 'not-allowed' : 'pointer',
                                                                backgroundColor: isSelected ? 'rgba(233, 174, 49, 0.08)' : isDark ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.01)',
                                                                border: isSelected ? '2px solid #E9AE31' : '1px solid ' + (isDark ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.06)'),
                                                                 borderRadius: '10px',
                                                                p: { xs: 1.2, sm: 1.4 },
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: 1.5,
                                                                transition: 'all 0.15s ease-in-out',
                                                                '&:hover': {
                                                                    backgroundColor: isSelected ? 'rgba(233, 174, 49, 0.1)' : 'rgba(100, 100, 100, 0.03)',
                                                                    borderColor: isSelected ? '#E9AE31' : 'rgba(233, 174, 49, 0.4)'
                                                                }
                                                            }}
                                                        >
                                                            <Box
                                                                sx={{
                                                                    backgroundColor: isSelected ? '#E9AE31' : 'rgba(100, 100, 100, 0.06)',
                                                                    borderRadius: '8px',
                                                                    p: { xs: 0.6, sm: 0.8 },
                                                                    flexShrink: 0,
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    color: isSelected ? '#FFFFFF' : reason.color,
                                                                    transition: 'all 0.15s'
                                                                }}
                                                            >
                                                                <IconComponent size={18} />
                                                            </Box>
                                                            <Typography sx={{ fontWeight: '600', color: currentTheme.textMain, fontSize: { xs: '15.5px', sm: '17px' }, lineHeight: 1.25 }}>
                                                                {t('reason_' + reason.id, reason.label)}
                                                            </Typography>
                                                        </Box>
                                                    </Grid>
                                                );
                                            })}
                                        </Grid>
                                    </Box>
                                )}
                            </Box>
                        )}
                    </Box>

                    {/* Bottom part: Buy Your Own QR Code & Call Buttons */}
                    <Box>
                        {!loading && vehicle && vehicle.activated !== false && (
                            <Box sx={{ textAlign: 'center', mb: 2, px: 2 }}>
                                <Typography variant="caption" sx={{ color: currentTheme.textMuted, display: 'block', mb: 1 }}>
                                    {isRTL
                                        ? 'للحصول على رمز الاستجابة السريعة الخاص بك، يرجى الشراء من هنا'
                                        : 'To get your own QR code please buy from here'}
                                </Typography>
                                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2 }}>
                                    <Box
                                        onClick={() => window.open('https://www.amazon.in/', '_blank')}
                                        sx={{
                                            width: { xs: '56px', sm: '64px' },
                                            height: { xs: '56px', sm: '64px' },
                                            aspectRatio: '1 / 1',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            backgroundColor: '#FFFFFF',
                                            borderRadius: '12px',
                                            cursor: 'pointer',
                                            transition: 'transform 0.15s',
                                            '&:hover': { transform: 'scale(1.05)' }
                                        }}
                                    >
                                        <Box component="img" src={AmazonIcon} alt="Buy on Amazon" sx={{ width: '80%', height: '80%', objectFit: 'contain' }} />
                                    </Box>
                                    <Box
                                        onClick={() => window.open('https://snoonu.com/', '_blank')}
                                        sx={{
                                            width: { xs: '56px', sm: '64px' },
                                            height: { xs: '56px', sm: '64px' },
                                            aspectRatio: '1 / 1',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            backgroundColor: '#FFFFFF',
                                            borderRadius: '12px',
                                            cursor: 'pointer',
                                            transition: 'transform 0.15s',
                                            '&:hover': { transform: 'scale(1.05)' }
                                        }}
                                    >
                                        <Box component="img" src={SnoonuIcon} alt="Buy on Snoonu" sx={{ width: '80%', height: '80%', objectFit: 'contain' }} />
                                    </Box>
                                </Box>
                            </Box>
                        )}

                        {!loading && vehicle && vehicle.activated !== false && (
                            <Box sx={{
                                position: { xs: 'fixed', md: 'absolute' },
                                bottom: 0,
                                left: 0,
                                right: 0,
                                backgroundColor: currentTheme.cardBg,
                                borderTop: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)'}`,
                                p: 1.5,
                                zIndex: 1100,
                                boxShadow: '0 -4px 20px rgba(0,0,0,0.1)',
                                minHeight: '80px',
                                display: 'flex',
                                alignItems: 'center'
                            }}>

                                {webrtcStatus !== 'idle' ? (
                                    <Box sx={{
                                        width: '100%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        py: 1
                                    }}>
                                        <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5, color: '#22c55e', fontSize: '13px', display: 'flex', alignItems: 'center', gap: 1 }}>
                                            {webrtcStatus === 'connecting' ? (
                                                <>
                                                    <CircularProgress size={14} color="inherit" />
                                                    Connecting Secure Web Call...
                                                </>
                                            ) : (
                                                <>
                                                    📞 Call in Progress (Masked)
                                                </>
                                            )}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: currentTheme.textMuted, mb: 1.5, fontSize: '11px' }}>
                                            Your number is masked and hidden from the owner. Do not close this browser window.
                                        </Typography>
                                        <Button
                                            variant="contained"
                                            onClick={handleWebRTCHangup}
                                            sx={{
                                                borderRadius: '20px',
                                                backgroundColor: '#ef4444',
                                                color: '#ffffff',
                                                fontWeight: 'bold',
                                                textTransform: 'none',
                                                px: 4,
                                                py: 0.8,
                                                fontSize: '13px',
                                                '&:hover': { backgroundColor: '#dc2626' }
                                            }}
                                        >
                                            Hang Up
                                        </Button>
                                    </Box>
                                ) : (
                                    showEmergencyMenu ? (
                                        <Box sx={{
                                            width: '100%',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            py: 1
                                        }}>
                                            <Typography variant="body2" sx={{ fontWeight: '800', mb: 1.5, color: '#ef4444', fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                                {isRTL ? 'خدمات الطوارئ الرسمية في دولة قطر (999)' : 'State of Qatar Emergency Services (999)'}
                                            </Typography>
                                            
                                            <Grid container spacing={1.5} sx={{ width: '100%', mb: 2 }}>
                                                {/* 999 Unified Emergency (Police, Ambulance, Fire Brigade) */}
                                                <Grid item xs={12}>
                                                    <Button
                                                        fullWidth
                                                        variant="contained"
                                                        onClick={() => handleEmergencyCall('999')}
                                                        sx={{
                                                            borderRadius: '14px',
                                                            py: 1.2,
                                                            px: 2.5,
                                                            backgroundColor: '#ef4444',
                                                            color: '#ffffff',
                                                            fontWeight: 'bold',
                                                            fontSize: '13px',
                                                            textTransform: 'none',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'space-between',
                                                            '&:hover': { backgroundColor: '#dc2626' }
                                                        }}
                                                    >
                                                        <Box sx={{ textAlign: isRTL ? 'right' : 'left' }}>
                                                            <Typography sx={{ fontWeight: 800, fontSize: '13.5px', lineHeight: 1.2, color: '#ffffff' }}>
                                                                {isRTL ? 'الشرطة والإسعاف والدفاع المدني (المطافئ)' : 'Police, Ambulance & Fire Brigade'}
                                                            </Typography>
                                                            <Typography sx={{ fontSize: '10.5px', color: 'rgba(255,255,255,0.85)' }}>
                                                                {isRTL ? 'الرقم الموحد للطوارئ بدولة قطر' : 'Qatar Unified Emergency Service'}
                                                            </Typography>
                                                        </Box>
                                                        <Typography sx={{ fontWeight: 900, fontSize: '18px', ml: 1, color: '#ffffff' }}>999</Typography>
                                                    </Button>
                                                </Grid>

                                                {/* 999 Ambulance Direct */}
                                                <Grid item xs={6}>
                                                    <Button
                                                        fullWidth
                                                        variant="contained"
                                                        onClick={() => handleEmergencyCall('999')}
                                                        sx={{
                                                            borderRadius: '14px',
                                                            py: 1.2,
                                                            px: 1.5,
                                                            backgroundColor: '#ea580c',
                                                            color: '#ffffff',
                                                            fontWeight: 'bold',
                                                            fontSize: '13px',
                                                            textTransform: 'none',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'space-between',
                                                            '&:hover': { backgroundColor: '#d97706' }
                                                        }}
                                                    >
                                                        <Box sx={{ textAlign: isRTL ? 'right' : 'left' }}>
                                                            <Typography sx={{ fontWeight: 800, fontSize: '12px', lineHeight: 1.2, color: '#ffffff' }}>
                                                                {isRTL ? 'الإسعاف (حمد)' : 'Ambulance (HMC)'}
                                                            </Typography>
                                                            <Typography sx={{ fontSize: '9.5px', color: 'rgba(255,255,255,0.85)' }}>
                                                                {isRTL ? 'طوارئ طبية' : 'Medical Direct'}
                                                            </Typography>
                                                        </Box>
                                                        <Typography sx={{ fontWeight: 900, fontSize: '14px', ml: 1, color: '#ffffff' }}>999</Typography>
                                                    </Button>
                                                </Grid>

                                                {/* 999 Fire Brigade */}
                                                <Grid item xs={6}>
                                                    <Button
                                                        fullWidth
                                                        variant="contained"
                                                        onClick={() => handleEmergencyCall('999')}
                                                        sx={{
                                                            borderRadius: '14px',
                                                            py: 1.2,
                                                            px: 1.5,
                                                            backgroundColor: '#b91c1c',
                                                            color: '#ffffff',
                                                            fontWeight: 'bold',
                                                            fontSize: '13px',
                                                            textTransform: 'none',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'space-between',
                                                            '&:hover': { backgroundColor: '#991b1b' }
                                                        }}
                                                    >
                                                        <Box sx={{ textAlign: isRTL ? 'right' : 'left' }}>
                                                            <Typography sx={{ fontWeight: 800, fontSize: '12px', lineHeight: 1.2, color: '#ffffff' }}>
                                                                {isRTL ? 'الدفاع المدني' : 'Fire Brigade'}
                                                            </Typography>
                                                            <Typography sx={{ fontSize: '9.5px', color: 'rgba(255,255,255,0.85)' }}>
                                                                {isRTL ? 'الحرائق والإنقاذ' : 'Civil Defence'}
                                                            </Typography>
                                                        </Box>
                                                        <Typography sx={{ fontWeight: 900, fontSize: '14px', ml: 1, color: '#ffffff' }}>999</Typography>
                                                    </Button>
                                                </Grid>
                                            </Grid>

                                            <Button
                                                variant="text"
                                                size="small"
                                                onClick={() => setShowEmergencyMenu(false)}
                                                sx={{ color: '#E9AE31', fontWeight: 'bold', textTransform: 'none', fontSize: '12px', minHeight: 0, p: 0.5 }}
                                            >
                                                {isRTL ? 'إلغاء / رجوع' : 'Cancel / Back'}
                                            </Button>
                                        </Box>
                                    ) : (
                                        <Grid container spacing={2} sx={{ maxWidth: '640px', mx: 'auto', width: '100%', direction: 'ltr' }}>
                                            <Grid item xs={6}>
                                                <Button
                                                    fullWidth
                                                    variant="contained"
                                                    onClick={handleCallOwnerClick}
                                                    startIcon={<Phone size={16} />}
                                                    sx={{
                                                        borderRadius: '16px',
                                                        py: 1.4,
                                                        backgroundColor: '#22c55e',
                                                        color: '#FFFFFF',
                                                        fontWeight: 'bold',
                                                        fontSize: { xs: '13px', sm: '15px' },
                                                        textTransform: 'none',
                                                        boxShadow: 'none',
                                                        '&:hover': {
                                                            backgroundColor: '#16a34a',
                                                            boxShadow: 'none'
                                                        }
                                                    }}
                                                >
                                                    {t('call_owner', 'Call Owner')}
                                                </Button>
                                            </Grid>
                                            <Grid item xs={6}>
                                                <Button
                                                    fullWidth
                                                    variant="contained"
                                                    onClick={() => setShowEmergencyMenu(true)}
                                                    startIcon={<PhoneCall size={16} />}
                                                    sx={{
                                                        borderRadius: '16px',
                                                        py: 1.4,
                                                        backgroundColor: '#ef4444',
                                                        color: '#FFFFFF',
                                                        fontWeight: 'bold',
                                                        fontSize: { xs: '13px', sm: '15px' },
                                                        textTransform: 'none',
                                                        boxShadow: 'none',
                                                        '&:hover': {
                                                            backgroundColor: '#dc2626',
                                                            boxShadow: 'none'
                                                        }
                                                    }}
                                                >
                                                    {t('emergency', 'Emergency')}
                                                </Button>
                                            </Grid>
                                        </Grid>
                                    )
                                )}
                            </Box>
                        )}
                    </Box>
                </Grid>
            </Grid>

            {/* Caller ID Input Prompt Dialog */}
            <Dialog 
                open={showCallerPrompt} 
                onClose={() => setShowCallerPrompt(false)}
                PaperProps={{
                    sx: {
                        borderRadius: '20px',
                        p: 1.5,
                        backgroundColor: currentTheme.cardBg,
                        color: currentTheme.textMain
                    }
                }}
            >
                <DialogTitle sx={{ fontWeight: 'bold', fontSize: '18px', color: currentTheme.textMain }}>
                    {isRTL ? 'اتصال آمن' : 'Secure Connection'}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 2, fontSize: '13px', color: currentTheme.textMuted }}>
                        {isRTL
                            ? 'لتوصيل المكالمة بأمان، يرجى إدخال رقم هاتفك (مع الرمز الدولي). سنقوم بإخفاء رقمك ولن يتم عرضه للمالك.'
                            : 'To connect call securely, please enter your mobile number (with country code, e.g. +91XXXXXXXXXX). We will mask your number so it is not shown to the owner.'}
                    </DialogContentText>
                    <TextField
                        autoFocus
                        margin="dense"
                        label={isRTL ? 'رقم هاتفك' : 'Your Phone Number'}
                        type="tel"
                        fullWidth
                        variant="outlined"
                        value={callerPhone}
                        onChange={(e) => setCallerPhone(e.target.value)}
                        placeholder="+97455551234"
                        disabled={connectingCall}
                        sx={{
                            '& label.Mui-focused': { color: '#E9AE31' },
                            '& .MuiOutlinedInput-root': {
                                '&.Mui-focused fieldset': { borderColor: '#E9AE31' },
                                borderRadius: '12px',
                                color: currentTheme.textMain,
                            }
                        }}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button 
                        onClick={() => setShowCallerPrompt(false)} 
                        disabled={connectingCall}
                        sx={{ color: currentTheme.textMuted, fontWeight: 'bold', textTransform: 'none' }}
                    >
                        {isRTL ? 'إلغاء' : 'Cancel'}
                    </Button>
                    <Button 
                        onClick={handleMaskedCall} 
                        disabled={connectingCall}
                        variant="contained"
                        sx={{ 
                            borderRadius: '16px',
                            backgroundColor: '#E9AE31', 
                            color: '#ffffff', 
                            fontWeight: 'bold', 
                            textTransform: 'none',
                            px: 3,
                            '&:hover': { backgroundColor: '#d97706' }
                        }}
                    >
                        {connectingCall ? <CircularProgress size={20} color="inherit" /> : (isRTL ? 'توصيل المكالمة' : 'Connect Call')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Modern Glassmorphic Active WebRTC Call UI Modal */}
            <Dialog
                open={webrtcStatus !== 'idle'}
                onClose={handleWebRTCHangup}
                PaperProps={{
                    sx: {
                        borderRadius: '28px',
                        background: 'linear-gradient(145deg, #111827 0%, #0f172a 100%)',
                        color: '#ffffff',
                        p: 3,
                        maxWidth: '360px',
                        width: '90%',
                        textAlign: 'center',
                        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.7)',
                        border: '1px solid rgba(255, 255, 255, 0.12)',
                        overflow: 'hidden'
                    }
                }}
            >
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 1 }}>
                    {/* Top Privacy Badge */}
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        px: 2,
                        py: 0.6,
                        borderRadius: '20px',
                        backgroundColor: 'rgba(34, 197, 94, 0.12)',
                        border: '1px solid rgba(34, 197, 94, 0.3)',
                        mb: 3
                    }}>
                        <ShieldCheck size={14} color="#22c55e" />
                        <Typography variant="caption" sx={{ color: '#22c55e', fontWeight: 700, fontSize: '11px', letterSpacing: '0.5px' }}>
                            {isRTL ? 'اتصال آمن ومشفر (مخفي الرقم)' : 'END-TO-END MASKED CALL'}
                        </Typography>
                    </Box>

                    {/* Animated Avatar / Ripple Icon */}
                    <Box sx={{
                        position: 'relative',
                        width: '90px',
                        height: '90px',
                        borderRadius: '50%',
                        backgroundColor: 'rgba(233, 174, 49, 0.15)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 2.5,
                        border: '2px solid #E9AE31',
                        boxShadow: '0 0 25px rgba(233, 174, 49, 0.3)'
                    }}>
                        <Car size={42} color="#E9AE31" />
                    </Box>

                    {/* Vehicle Info */}
                    <Typography variant="h6" sx={{ fontWeight: 800, color: '#ffffff', mb: 0.5, fontSize: '18px' }}>
                        {vehicle?.vehicleNumber || ''}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', mb: 2, fontSize: '13px', fontWeight: 600 }}>
                        {isRTL ? 'مالك المركبة' : 'Car Owner'}
                    </Typography>

                    {/* Live Call Status & Timer */}
                    <Box sx={{ mb: 3 }}>
                        {webrtcStatus === 'connecting' ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center' }}>
                                <CircularProgress size={16} sx={{ color: '#E9AE31' }} />
                                <Typography variant="body2" sx={{ color: '#E9AE31', fontWeight: 700, fontSize: '13px' }}>
                                    {isRTL ? 'جاري الاتصال بالمالك...' : 'Dialing Owner...'}
                                </Typography>
                            </Box>
                        ) : (
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                                <Typography variant="h4" sx={{ fontWeight: 800, color: '#22c55e', fontFamily: 'monospace', letterSpacing: '1px' }}>
                                    {formatCallTime(callTimer)}
                                </Typography>
                                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '11px' }}>
                                    {isRTL ? 'متصل • الحد الأقصى للمكالمة دقيقة واحدة (01:00)' : 'Connected • 1-Min Max Call Limit (01:00)'}
                                </Typography>
                            </Box>
                        )}
                    </Box>

                    {/* Call Controls */}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 3, width: '100%', mt: 1 }}>
                        {/* Mute Button */}
                        <Button
                            onClick={toggleMute}
                            disabled={webrtcStatus !== 'connected'}
                            sx={{
                                width: '54px',
                                height: '54px',
                                minWidth: '54px',
                                borderRadius: '50%',
                                backgroundColor: isMuted ? '#ef4444' : 'rgba(255,255,255,0.1)',
                                color: '#ffffff',
                                '&:hover': {
                                    backgroundColor: isMuted ? '#dc2626' : 'rgba(255,255,255,0.2)'
                                }
                            }}
                        >
                            {isMuted ? <MicOff size={22} /> : <Mic size={22} />}
                        </Button>

                        {/* End Call Button */}
                        <Button
                            onClick={handleWebRTCHangup}
                            sx={{
                                width: '64px',
                                height: '64px',
                                minWidth: '64px',
                                borderRadius: '50%',
                                backgroundColor: '#ef4444',
                                color: '#ffffff',
                                boxShadow: '0 0 20px rgba(239, 68, 68, 0.4)',
                                '&:hover': {
                                    backgroundColor: '#dc2626',
                                    boxShadow: '0 0 25px rgba(220, 38, 38, 0.6)'
                                }
                            }}
                        >
                            <PhoneOff size={26} />
                        </Button>
                    </Box>
                </Box>
            </Dialog>

            {/* Message Sent Confirmation Popup Dialog */}
            <Dialog
                open={messageSentPopup}
                onClose={() => setMessageSentPopup(false)}
                PaperProps={{
                    sx: {
                        borderRadius: '24px',
                        p: 2.5,
                        maxWidth: '360px',
                        width: '90%',
                        textAlign: 'center',
                        backgroundColor: currentTheme.cardBg,
                        color: currentTheme.textMain,
                        boxShadow: '0 20px 40px rgba(0,0,0,0.3)'
                    }
                }}
            >
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 1 }}>
                    <Box sx={{
                        width: '64px',
                        height: '64px',
                        borderRadius: '50%',
                        backgroundColor: 'rgba(34, 197, 94, 0.15)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 2,
                        color: '#22c55e'
                    }}>
                        <CheckCircle2 size={36} />
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 800, mb: 1, fontSize: '18px' }}>
                        {isRTL ? 'تم إرسال الرسالة!' : 'Message Sent!'}
                    </Typography>
                    <Typography variant="body2" sx={{ color: currentTheme.textMuted, mb: smsRemainingCount !== null ? 1.5 : 3, fontSize: '13px', px: 1 }}>
                        {isRTL
                            ? 'تم إرسال التنبيه عبر SMS مباشرة إلى مالك المركبة. شكراً لإبلاغه!'
                            : 'An SMS alert has been dispatched directly to the vehicle owner. Thank you for notifying them!'}
                    </Typography>
                    {smsRemainingCount !== null && (
                        <Typography variant="caption" sx={{ color: '#E9AE31', mb: 3, fontSize: '12px', px: 1, fontWeight: 600 }}>
                            {isRTL
                                ? `يمكنك إرسال ${smsRemainingCount} رسالة أخرى كحد أقصى خلال الـ 15 دقيقة القادمة.`
                                : `You have ${smsRemainingCount} more message${smsRemainingCount === 1 ? '' : 's'} you can send within the next 15 minutes.`}
                        </Typography>
                    )}
                    <Button
                        fullWidth
                        variant="contained"
                        onClick={() => setMessageSentPopup(false)}
                        sx={{
                            borderRadius: '16px',
                            py: 1.2,
                            backgroundColor: '#22c55e',
                            color: '#ffffff',
                            fontWeight: 700,
                            fontSize: '14px',
                            textTransform: 'none',
                            '&:hover': { backgroundColor: '#16a34a' }
                        }}
                    >
                        {isRTL ? 'تم' : 'Done'}
                    </Button>
                </Box>
            </Dialog>

            {/* Rate Limit Warning Popup Dialog */}
            <Dialog
                open={rateLimitModal.open}
                onClose={() => setRateLimitModal({ ...rateLimitModal, open: false })}
                PaperProps={{
                    sx: {
                        borderRadius: '24px',
                        p: 2.5,
                        maxWidth: '380px',
                        width: '90%',
                        textAlign: 'center',
                        backgroundColor: currentTheme.cardBg,
                        color: currentTheme.textMain,
                        boxShadow: '0 20px 40px rgba(0,0,0,0.3)'
                    }
                }}
            >
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 1 }}>
                    <Box sx={{
                        width: '64px',
                        height: '64px',
                        borderRadius: '50%',
                        backgroundColor: 'rgba(239, 68, 68, 0.15)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 2,
                        color: '#ef4444'
                    }}>
                        <Clock size={34} />
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 800, mb: 1, fontSize: '18px' }}>
                        {rateLimitModal.type === 'call' 
                            ? (isRTL ? 'تم الوصول لجميع محاولات الاتصال' : 'Call Limit Reached') 
                            : (isRTL ? 'تم الوصول لجميع محاولات الرسائل' : 'Message Limit Reached')}
                    </Typography>
                    <Typography variant="body2" sx={{ color: currentTheme.textMuted, mb: 3, fontSize: '13px', px: 1 }}>
                        {isRTL
                            ? `لحماية المالك من الإزعاج، يمكنك ${rateLimitModal.type === 'call' ? `إجراء كحد أقصى ${rateLimitModal.limit || 2} مكالمتين` : `إرسال كحد أقصى ${rateLimitModal.limit || 3} رسائل`} كل 15 دقيقة.`
                            : `To prevent spam, you can only ${rateLimitModal.type === 'call' ? `make ${rateLimitModal.limit || 2} calls` : `send ${rateLimitModal.limit || 3} messages`} every 15 minutes.`}
                        <br />
                        <span style={{ marginTop: '8px', display: 'inline-block' }}>
                            <strong>{isRTL ? `يرجى المحاولة مرة أخرى بعد ${rateLimitModal.remainingMins || 1} دقيقة.` : `Please try again in ${rateLimitModal.remainingMins || 1} minute(s).`}</strong>
                        </span>
                    </Typography>
                    <Button
                        fullWidth
                        variant="contained"
                        onClick={() => setRateLimitModal({ ...rateLimitModal, open: false })}
                        sx={{
                            borderRadius: '16px',
                            py: 1.2,
                            backgroundColor: '#E9AE31',
                            color: '#ffffff',
                            fontWeight: 700,
                            fontSize: '14px',
                            textTransform: 'none',
                            '&:hover': { backgroundColor: '#d99b25' }
                        }}
                    >
                        {isRTL ? 'حسناً' : 'Understood'}
                    </Button>
                </Box>
            </Dialog>

            {/* Contact Selection Modal (when vehicle has multiple numbers) */}
            <Dialog
                open={showContactSelector}
                onClose={() => setShowContactSelector(false)}
                PaperProps={{
                    sx: {
                        borderRadius: '24px',
                        p: 2.5,
                        maxWidth: '380px',
                        width: '90%',
                        textAlign: 'center',
                        backgroundColor: currentTheme.cardBg,
                        color: currentTheme.textMain,
                        boxShadow: '0 20px 40px rgba(0,0,0,0.3)'
                    }
                }}
            >
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 1 }}>
                    <Box sx={{
                        width: '56px',
                        height: '56px',
                        borderRadius: '50%',
                        backgroundColor: 'rgba(34, 197, 94, 0.15)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 2,
                        color: '#22c55e'
                    }}>
                        <Phone size={28} />
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 800, mb: 0.5, fontSize: '18px' }}>
                        {isRTL ? 'اختر جهة الاتصال للاتصال بها' : 'Select Contact to Call'}
                    </Typography>
                    <Typography variant="body2" sx={{ color: currentTheme.textMuted, mb: 3, fontSize: '12px' }}>
                        {isRTL 
                            ? <span>تم تسجيل عدة أرقام تواصل للمركبة <b dir="ltr" style={{ display: 'inline-block' }}>{vehicle?.vehicleNumber || ''}</b></span>
                            : <span>Multiple contact numbers are registered for vehicle <b dir="ltr" style={{ display: 'inline-block' }}>{vehicle?.vehicleNumber || ''}</b>.</span>}
                    </Typography>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, width: '100%' }}>
                        {/* Primary Contact Option */}
                        <Box
                            onClick={() => {
                                setShowContactSelector(false);
                                initiateCallFlow('primary');
                            }}
                            sx={{
                                width: '100%',
                                borderRadius: '16px',
                                py: 1.5,
                                px: 2.5,
                                backgroundColor: '#22c55e',
                                color: '#ffffff',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 2,
                                direction: isRTL ? 'rtl' : 'ltr',
                                transition: 'all 0.2s ease',
                                '&:hover': { backgroundColor: '#16a34a' }
                            }}
                        >
                            <Phone size={22} style={{ flexShrink: 0 }} />
                            <Box sx={{ textAlign: isRTL ? 'right' : 'left', flex: 1 }}>
                                <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '14px', color: '#ffffff', lineHeight: 1.3 }}>
                                    {isRTL ? 'مالك المركبة (الرئيسي)' : 'Car Owner (Primary)'}
                                </Typography>
                                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.85)', fontSize: '11px', display: 'block', mt: 0.2 }}>
                                    {isRTL ? 'جهة الاتصال الرئيسية المسجلة' : 'Primary registered contact'}
                                </Typography>
                            </Box>
                        </Box>

                        {/* Secondary Contact Option */}
                        <Box
                            onClick={() => {
                                setShowContactSelector(false);
                                initiateCallFlow('secondary');
                            }}
                            sx={{
                                width: '100%',
                                borderRadius: '16px',
                                py: 1.5,
                                px: 2.5,
                                border: '2px solid #E9AE31',
                                backgroundColor: isDark ? 'rgba(233, 174, 49, 0.08)' : 'rgba(233, 174, 49, 0.05)',
                                color: currentTheme.textMain,
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 2,
                                direction: isRTL ? 'rtl' : 'ltr',
                                transition: 'all 0.2s ease',
                                '&:hover': { borderColor: '#d97706', backgroundColor: 'rgba(233, 174, 49, 0.12)' }
                            }}
                        >
                            <PhoneCall size={22} color="#E9AE31" style={{ flexShrink: 0 }} />
                            <Box sx={{ textAlign: isRTL ? 'right' : 'left', flex: 1 }}>
                                <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '14px', color: currentTheme.textMain, lineHeight: 1.3 }}>
                                    {getSecondaryContactTitle()}
                                </Typography>
                                <Typography variant="caption" sx={{ color: currentTheme.textMuted, fontSize: '11px', display: 'block', mt: 0.2 }}>
                                    {isRTL ? 'جهة الاتصال الثانوية المسجلة' : 'Secondary registered contact'}
                                </Typography>
                            </Box>
                        </Box>
                    </Box>

                    <Button
                        onClick={() => setShowContactSelector(false)}
                        sx={{ mt: 2, color: currentTheme.textMuted, fontWeight: 700, fontSize: '12px', textTransform: 'none' }}
                    >
                        {isRTL ? 'إلغاء' : 'Cancel'}
                    </Button>
                </Box>
            </Dialog>

            {/* Bottom Footer */}
            <HomeFooter />
        </Box>
    );
}
